import React, { useContext, useEffect, useState } from "react";
import { CalendarIcon, CheckCircle, Users, Briefcase, UserCheck, MapPin } from "lucide-react";
import CircularProgress from "@mui/material/CircularProgress";
import { Typography } from "@material-ui/core";
import { Tabs, TabsList, TabsTrigger } from "../../components/Tabs/Tabs";
import DateAndTimeSelector from "./DateTimeSelector";
import styles from "./calendar.module.scss";
import { useMediaQuery } from "@mui/material";
import moment from "moment";
import { useLocation, useNavigate } from "react-router";
import { getFormatedDate } from "../../services/helpers";
import SingleSelectNew from "../../components/SelectField/SingleSelectNew";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import dayjs from 'dayjs';
import { AppointmentModal } from "./Models/AppointmentModel";
import AuthContext from "../../hooks/authContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import SingleSelectSearch from "../../components/SelectField/SelectSearch";

const AppointmentsCard = ({ appointmentProps, DateTimeProps }) => {

  const { clientName, shiftNames, empNames, formData, setUnitNumber, unitNumber, appointmentId, appointmentType, setAppointmentType, displayStep, setDisplayStep, handleClientNameChange, checkInTime, checkOutTime, clientNameList,
    handleShiftNameChange, shiftNamesList, handleAddAppointments, loader, hasTimeZoneConflict, recurringDates, upcomingAppoinments, selectedClient, selectedEmployee, startTime, endTime,
    selectedDate, handleRemoveDate, errors, employeeNameList, handleEmpNameChange, appoinments, frequencyData, numberOfTime, settingsData, setErrors, clientDetails, empDetails, selectedIds, editAppointmentData, conflictMap, setConflictMap
  } = appointmentProps;

  const auth = useContext(AuthContext);
  const isMobile = useMediaQuery('(max-width:767px)');
  const isLandscape = useMediaQuery('(min-width: 771px) and (max-width: 940px)');
  const navigate = useNavigate();
  const isModalOpen = auth.isModalOpen;
  const location = useLocation();
  const mode = location.state?.mode;
  const [isPast, setIsPast] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState();
  const selectedAppointments = auth.selectedAppointment;
  const setIsModalOpen = auth.setIsModalOpen;
  const initialSelectionComplete = clientName !== "All" && shiftNames !== "Any" && empNames !== "All";
  const capitalizeFirst = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

  const formats = ['YYYY-MM-DD h:mm A', 'YYYY-MM-DD h:mm a', 'YYYY-MM-DD hh:mm A', 'YYYY-MM-DD hh:mm a'];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return new Intl.DateTimeFormat('en-US', options).format(date);
  };

  const getNowInTimezone = (timeZone) => {
    return new Date(new Date().toLocaleString("en-US", { timeZone }));
  };

  const tz =
    selectedAppointments?.apoointmentDetails?.calendarTimezone ||
    "America/New_York";
  const nowInAppointmentTZ = getNowInTimezone(tz);

  const findMatchingAppointments = () => {
    if (!selectedAppointments) return [];

    const details = selectedAppointments.apoointmentDetails;

    if (details.type === "Recurring Appointment") {
      return appoinments?.filter((appointment) => {
        const d = appointment?.apoointmentDetails;

        return (
          d.appointmentTypeID === details.appointmentTypeID &&
          d.frequency === details.frequency &&
          d.duration === details.duration &&
          d.clientId === details.clientId &&
          appointment.userId === selectedAppointments.userId &&
          appointment.companyId === selectedAppointments.companyId &&
          appointment.shiftId === selectedAppointments.shiftId &&
          d.shiftName === details.shiftName &&
          d.time === details.time &&
          d.endTime === details.endTime
        );
      });
    }

    return [selectedAppointments];
  };

  const getFutureAppointments = (appointments) => {
    return appointments?.filter((a) => {
      const details = a.apoointmentDetails;
      const timeZone = details?.calendarTimezone || "America/New_York";

      const nowInTZ = getNowInTimezone(timeZone);

      const start = new Date(details.appointmentDateTime);
      const end = new Date(start.getTime() + details.duration * 60 * 1000);

      // const start = new Date(details.appointmentDateTime);
      // const end = new Date(new Date(`${details?.date} ${details?.endTime}`).toLocaleString("en-US", { timeZone }));
      const hasCheckIn = !!a?.checkInDateTime;

      if (end < nowInTZ) return false;

      if (hasCheckIn) return false;

      return true;
    });
  };

  const matchingAppointments = findMatchingAppointments();
  const futureAppointments = getFutureAppointments(matchingAppointments);

  const ignoreAppointmentIds = new Set(futureAppointments?.map((a) => a.id));

  const checkConflictForDate = (formattedDate) => {
    const conflictingAppointment = appoinments?.find(
      (appointment) => {
        if (ignoreAppointmentIds.has(appointment.id)) {
          return false;
        }
        const details = appointment?.apoointmentDetails;
        if (!details || details.date !== formattedDate) return false;

        const isSameClient = details?.clientId === clientName;
        const isSameEmployee = details?.userId === empNames;

        // if (!isSameClient && !isSameEmployee) return false;
        if (!isSameEmployee) return false;

        const baseDate = dayjs(formattedDate).format("YYYY-MM-DD");

        let existingStart = dayjs(`${baseDate} ${details?.time}`, formats, true);
        let existingEnd = dayjs(`${baseDate} ${details?.endTime}`, formats, true);
        let newStart = dayjs(`${baseDate} ${startTime}`, formats, true);
        let newEnd = dayjs(`${baseDate} ${endTime}`, formats, true);

        // Fallback to loose parsing (Safari-safe)
        if (!existingStart.isValid())
          existingStart = dayjs(`${baseDate} ${details?.time}`);
        if (!existingEnd.isValid())
          existingEnd = dayjs(`${baseDate} ${details?.endTime}`);
        if (!newStart.isValid()) newStart = dayjs(`${baseDate} ${startTime}`);
        if (!newEnd.isValid()) newEnd = dayjs(`${baseDate} ${endTime}`);

        if (existingEnd.isSameOrBefore(existingStart)) {
          existingEnd = existingEnd.add(1, "day");
        }
        if (newEnd.isSameOrBefore(newStart)) {
          newEnd = newEnd.add(1, "day");
        }

        if (
          !existingStart.isValid() ||
          !existingEnd.isValid() ||
          !newStart.isValid() ||
          !newEnd.isValid()
        ) {
          return false;
        }

        const isOverlap =
          newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart);
        // const isOverlap = (newStart.isAfter(existingStart) && newStart.isBefore(existingEnd)) || (newEnd.isAfter(existingStart) && newEnd.isBefore(existingEnd))
        //   || (existingStart.isAfter(newStart) && existingStart.isBefore(newEnd)) || (existingEnd.isAfter(newStart) && existingEnd.isBefore(newEnd));

        return isOverlap;
      }
    );
    return conflictingAppointment || null;
  };

  const getUserConflictGroup = () => {
    const role = auth?.userProfile?.role;
    const employeeRole = auth?.userProfile?.employeerole;

    const roles = Array.isArray(role) ? role?.map(r => r?.toLowerCase()) : role ? [role?.toLowerCase()] : [];

    const isEmployee = roles.includes("employee");
    const isLeadManager = roles.includes("companyuser");
    const isAdmin = isEmployee && employeeRole === "Admin";
    const isSupport = isEmployee && employeeRole === "Support";

    if (isLeadManager) return "Leads / Managers";
    if (isAdmin || isSupport) return "Team Admin / Support";
    if (isEmployee || isLeadManager || isAdmin || isSupport)
      return "All Employees";

    return "None";
  };

  const conflictGroup = getUserConflictGroup();

  const canUserBypassConflict = () => {
    const userGroup = getUserConflictGroup();
    const allowedGroup = settingsData?.overlapping;

    if (!allowedGroup || allowedGroup === "None") {
      return false;
    }

    if (userGroup === allowedGroup) return true;

    if (allowedGroup === "All Employees") {
      return userGroup === "All Employees" ||
        userGroup === "Team Admin / Support" ||
        userGroup === "Leads / Managers";
    }
    return false;
  };


  const hasAnyConflict = Object.values(conflictMap || {})?.some(v => v && typeof v === "object");
  const conflictExists = appointmentType === "One Time" ? upcomingAppoinments?.length > 0 : hasAnyConflict;
  const isAllowedToBypass = canUserBypassConflict();

  const disableBooking = conflictExists && !isAllowedToBypass;
  const showRedMessage = settingsData?.overlapping === "None";

  useEffect(() => {
    if (!recurringDates || recurringDates.length === 0) {
      setConflictMap({});
      return;
    }

    const map = {};

    recurringDates?.forEach(date => {
      const formattedDate = moment(date).format("MMMM D, YYYY");
      const conflictData = checkConflictForDate(formattedDate);
      map[formattedDate] = conflictData;
    });

    setConflictMap(map);
  }, [recurringDates]);

  useEffect(() => {
    if (!selectedDate) {
      setIsPast(false);
      return;
    }

    const today = dayjs()?.startOf("day");
    const isPastDate = selectedDate.startOf("day").isBefore(today);
    setIsPast(isPastDate);

  }, [selectedDate, startTime]);

  return (
    <>
      <div className={isMobile ? "d-flex flex-direction-column dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] dark:border-0" : "dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] dark:border-0 d-flex flex-space-between w-100 ph-32 pv-24"}>
        <div className="width50 bg-white">
          <Card className={`${styles.card} dark:rounded-none dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] dark:border-0`}>
            <div className="p-24">
              <Typography variant="body" className={`${styles.cardTitle} dark:text-white`}>Selection Steps</Typography>
            </div>
            <CardContent className="p-0">
              <div className="space-y-1">
                <Button
                  variant="ghost"
                  className={`w-full justify-start px-6 py-4 text-left rounded-none border-l-4 ${displayStep === 1
                    ? "border-l-green-600 bg-green-50 text-green-700 font-medium dark:bg-gray-800 dark:text-white"
                    : clientName !== "All"
                      ? "border-l-green-200 bg-white text-gray-600 dark:bg-[#0F1729] dark:text-white"
                      : "border-l-transparent"
                    }`}
                  onClick={() => setDisplayStep(1)}
                >
                  <div className="flex items-center">
                    <Users className="h-5 w-5 mr-12" />
                    <div>
                      <div className="font-medium">1. Client Selection</div>
                      <div className="text-xs text-muted-foreground">
                        {clientName !== "All" ? selectedClient?.label : "Select client"}
                      </div>
                    </div>
                    {clientName !== "All" && (
                      <CheckCircle className="h-4 w-4 ml-8 mb-10 text-green-600" />
                    )}
                  </div>
                </Button>

                <Button
                  variant="ghost"
                  className={`w-full justify-start px-6 py-4 text-left rounded-none border-l-4 ${displayStep === 2
                    ? "border-l-green-600 bg-green-50 text-green-700 font-medium dark:bg-gray-800 dark:text-white"
                    : shiftNames !== "Any"
                      ? "border-l-green-200 bg-white text-gray-600 dark:bg-[#0F1729] dark:text-white"
                      : "border-l-transparent"
                    }`}
                  onClick={() => setDisplayStep(2)}
                  disabled={clientName === "All"}
                >
                  <div className="flex items-center">
                    <Briefcase className="h-5 w-5 mr-12" />
                    <div>
                      <div className="font-medium">2. Shift Selection</div>
                      <div className="text-xs text-muted-foreground">
                        {shiftNames !== "Any" ? shiftNames : "Select shift"}
                      </div>
                    </div>
                    {shiftNames !== "Any" && (
                      <CheckCircle className="h-4 w-4 ml-8 mb-10 text-green-600" />
                    )}
                  </div>
                </Button>

                <Button
                  variant="ghost"
                  className={`w-full justify-start px-6 py-4 text-left rounded-none border-l-4 ${displayStep === 3
                    ? "border-l-green-600 bg-green-50 text-green-700 font-medium dark:bg-gray-800 dark:text-white"
                    : empNames !== "All"
                      ? "border-l-green-200 bg-white text-gray-600 dark:bg-[#0F1729] dark:text-white"
                      : "border-l-transparent"
                    }`}
                  onClick={() => setDisplayStep(3)}
                  disabled={clientName === "All" || shiftNames === "Any"}
                >
                  <div className="flex items-center">
                    <UserCheck className="h-5 w-5 mr-12" />
                    <div>
                      <div className="font-medium">3. Employee Selection</div>
                      <div className="text-xs text-muted-foreground">
                        {empNames !== "All" ? selectedEmployee?.label : "Select employee"}
                      </div>
                    </div>
                    {empNames !== "All" && (
                      <CheckCircle className="h-4 w-4 ml-8 mb-10 text-green-600" />
                    )}
                  </div>
                </Button>

                <Button
                  variant="ghost"
                  className={`w-full justify-start px-6 py-4 text-left rounded-none border-l-4 ${displayStep === 4
                    ? "border-l-green-600 bg-green-50 text-green-700 font-medium dark:bg-gray-800 dark:text-white"
                    : "border-l-transparent"
                    }`}
                  onClick={() => setDisplayStep(4)}
                  disabled={!initialSelectionComplete}
                >
                  <div className="flex items-center">
                    <CalendarIcon className="h-5 w-5 mr-12" />
                    <div>
                      <div className="font-medium">4. Appointment Details</div>
                      <div className="text-xs text-muted-foreground">
                        Select date and time
                      </div>
                    </div>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className={isMobile ? "w-100 mt-20" : "ml-20 w-100"}>
          <Card className={`${styles.card} bg-white dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] dark:border-0`}>
            <div className="p-24">
              <div className={`${styles.h3} dark:text-white`}>
                {displayStep === 1 && "Select Client"}
                {displayStep === 2 && "Select Shift"}
                {displayStep === 3 && "Select Employee"}
                {displayStep === 4 && "Appointment Details"}
              </div>
              <div className={`${styles.p} dark:text-white`}>
                {displayStep === 1 && "Choose the client for this appointment."}
                {displayStep === 2 && "Select which shift this appointment belongs to."}
                {displayStep === 3 && "Assign an employee for this appointment."}
                {displayStep === 4 && "Configure when this appointment will take place."}
              </div>
            </div>

            <CardContent className={styles.cardContent}>
              {displayStep === 1 && (
                <>
                  <Typography variant="body" className="text-[#323744] dark:text-white" style={{ fontSize: "16px", fontWeight: "600" }}>Client</Typography>
                  <div className={isMobile ? "mb-20 mt-10 w-100" : "mv-10"}>
                    <SingleSelectNew
                      value={clientName}
                      placeholder={"Client Name"}
                      onChange={handleClientNameChange}
                      contentClass="bg-white text-black border-gray-300 dark:bg-[#2c3440] dark:text-white dark:border-[#464c59]"
                      itemClass="hover:bg-gray-100 dark:hover:bg-[#3c4351]"
                      options={clientNameList}
                      disabled={!!checkInTime || !!checkOutTime || !!appointmentId}
                    />
                    {errors.clientName && <div className="mt-4" style={{ color: '#fc8600' }}>Choose One</div>}

                    {formData && formData?.clients && (
                      <div className="mt-12 p-12 bg-slate-50 rounded-lg border dark:bg-gray-800">
                        <div className="flex items-start space-x-2">
                          <MapPin className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900 mb-8 dark:text-white">Client Address</h4>
                            <div className="text-sm text-gray-600 dark:text-white space-y-1">
                              {formData?.clients?.addressInfo?.address && (
                                <div>{formData?.clients?.addressInfo?.address}</div>
                              )}
                              <div className="flex flex-wrap gap-1">
                                {formData?.clients?.addressInfo?.city && (
                                  <span>{formData?.clients?.addressInfo?.city}</span>
                                )}
                                {formData?.clients?.addressInfo?.state && (
                                  <span>{formData?.clients?.addressInfo?.state}</span>
                                )}
                                {formData?.clients?.addressInfo?.zip && (
                                  <span>{formData?.clients?.addressInfo?.zip}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-12">
                          <Label className="text-sm font-medium">Unit Number (Optional)</Label>
                          <Input
                            value={unitNumber}
                            placeholder="e.g., Unit 5B, Apt 123, Suite 200"
                            className="h-10 mv-5"
                            onChange={(e) => setUnitNumber(e.target.value)}
                            disabled={!!appointmentId}
                          />
                          <div className="text-xs text-left">
                            Add a unit, apartment, or suite number if applicable
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
              {displayStep === 2 && (
                <>
                  <Typography variant="body" className="text-[#323744] dark:text-white" style={{ fontSize: "16px", fontWeight: "600" }}>Shift</Typography>
                  <div className={isMobile ? "mb-20 mt-10 w-100" : "mv-10"}>
                    <SingleSelectNew
                      value={shiftNames}
                      placeholder={"Shift Name"}
                      contentClass="bg-white text-black border-gray-300 dark:bg-[#2c3440] dark:text-white dark:border-[#464c59]"
                      itemClass="hover:bg-gray-100 dark:hover:bg-[#3c4351]"
                      onChange={handleShiftNameChange}
                      options={shiftNamesList}
                      disabled={!!checkInTime || !!checkOutTime || clientName === "All" || !!appointmentId}
                    />
                    {errors.shiftName && <div className="mt-4" style={{ color: '#fc8600' }}>Choose One</div>}

                    {clientName !== "All" && shiftNamesList?.length > 0 &&
                      (() => {
                        const actualShifts = shiftNamesList.filter(opt => !["Any", "No Shift"].includes(opt.value));
                        return actualShifts.length === 0 || actualShifts.every(opt => opt.disabled);
                      })() && (
                        <div className="mt-8 text-sm text-gray-600 dark:text-gray-400">
                          No Tasks Assigned.
                          <span
                            className="ml-4 text-blue-600 cursor-pointer hover:underline font-medium"
                            onClick={() => navigate(`/peoples/createClient/${clientName}`, { state: { jumpToStep: 4 } })}
                          >
                            Please add Tasks here.
                          </span>
                        </div>
                      )}

                    {formData && formData?.task && (
                      <div className="mt-16 p-16 bg-slate-50 rounded-lg border dark:bg-gray-800">
                        <div className="flex items-start space-x-2">
                          <Briefcase className="h-5 w-5 text-gray-500 mt-2 flex-shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900 mb-8 dark:text-white">Related Tasks</h4>
                            <div className="grid grid-cols-3 gap-2 text-[12px] 2xl:text-[14px] text-gray-600">
                              {formData?.task?.map((task, index) => (
                                <div key={index} className="text-left dark:text-white">
                                  • {task?.title}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
              {displayStep === 3 && (
                <>
                  <Typography variant="body" className="text-[#323744] dark:text-white" style={{ fontSize: "16px", fontWeight: "600" }}>Employee</Typography>
                  <div className={isMobile ? "mb-20 mt-10 w-100" : "mv-10"}>
                    {/* <SingleSelectNew
                                        value={empNames}
                                        placeholder={"Employee Name"}
                                        onChange={handleEmpNameChange}
                                        options={employeeNameList}
                                        disabled={!!checkInTime || !!checkOutTime || shiftNames === "Any"}
                                    /> */}
                    <SingleSelectSearch
                      value={empNames}
                      placeholder={"Employee Name"}
                      contentClass="bg-white text-black border-gray-300 dark:bg-[#2c3440] dark:text-white dark:border-[#464c59]"
                      itemClass="dark:hover:bg-[#3c4351]"
                      onChange={handleEmpNameChange}
                      options={employeeNameList}
                      disabled={!!checkInTime || !!checkOutTime || shiftNames === "Any" || !!appointmentId}
                    // showAssignedHeader={true}
                    />
                    {errors.empName && <div className="mt-4" style={{ color: '#fc8600' }}>Choose One</div>}
                  </div>
                </>
              )}
              {displayStep === 4 && (
                <>
                  {appointmentId && (
                    <div className="mb-16 p-16 bg-slate-50 rounded-lg border dark:bg-gray-800 text-[#fc8600] text-md font-weight-600">
                      To reschedule this appointment, please update the appointment date, start time, or end time.
                    </div>
                  )}
                  <Typography variant="body" style={{ fontSize: "16px", fontWeight: "600" }} className="text-[#323744] dark:text-white">Appointment Type</Typography>
                  <div className="mt-8">
                    <Tabs
                      defaultValue={appointmentType}
                      onValueChange={(value) => {
                        if (appointmentId) return;
                        setAppointmentType(value);
                      }}>
                      <TabsList>
                        <TabsTrigger value="One Time" disabled={!!appointmentId}>
                          <span className="w-100">One Time</span>
                        </TabsTrigger>
                        <TabsTrigger value="Recurring Appointment" disabled={!!appointmentId}>
                          <span className="w-100">Recurring Appointment</span>
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                  <div>
                    <DateAndTimeSelector
                      DateTimeProps={DateTimeProps}
                    />

                    {appointmentType === "Recurring Appointment" && hasAnyConflict && (
                      <div
                        style={{
                          background: settingsData?.overlapping === "None" ? "#ffe5e5" : "#fff1e0",
                          color: settingsData?.overlapping === "None" ? "#FF0000" : "#fc8600",
                          border: `1px solid ${settingsData?.overlapping === "None" ? "#ffb3b3" : "#ffd2a3"}`,
                          padding: "12px 16px",
                          borderRadius: "8px",
                          marginBottom: "12px",
                          marginTop: "14px",
                          fontWeight: 500
                        }}
                      >
                        {showRedMessage
                          ? "There are one or more scheduling conflicts with this appointment. Your company does not allow overlapping appointments. Please update your schedule."
                          : "There are one or more scheduling conflicts with this appointment. Please review them before you proceed."}
                      </div>
                    )}

                    {appointmentType === "Recurring Appointment" && Array.isArray(recurringDates) && recurringDates?.length > 0 && (
                      <Card className={`${styles.appointmentSummaryCard} dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] dark:border-0`}>
                        <CardContent className="p-16">
                          <div>
                            {appointmentType === "Recurring Appointment" && Array.isArray(recurringDates) && recurringDates?.length > 0 ? (
                              recurringDates?.map((date, index) => {
                                const formattedDate = moment(date).format("MMMM D, YYYY");
                                const hasConflict = conflictMap[formattedDate];
                                return (
                                  <div
                                    key={index}
                                    className="d-flex flex-space-between flex-center"
                                    style={{ width: isMobile || isLandscape ? "100%" : "95%" }}
                                  >
                                    <div className={`${isMobile || isLandscape ? "mr-30 mv-10" : "mv-10"} dark:text-white text-[#020817]`} style={{ fontWeight: "500", fontSize: "14px" }}>
                                      {`${settingsData?.timeFormat === '24hrs' ? moment(startTime, 'h:mm A').format('HH:mm') : startTime} on ${moment(date).format("MMMM DD, YYYY")} (${moment(date).format('ddd')})`}
                                    </div>
                                    <div
                                      className="d-flex flex-center appointmentConflictsWidht"
                                    >
                                      <div
                                        className={`${isMobile || isLandscape ? "mr-15 link" : "link"}`}
                                        onClick={() => handleRemoveDate(date)}
                                        style={{ cursor: "pointer" }}
                                      >
                                        (remove)
                                      </div>

                                      {hasConflict && !isMobile && !isLandscape ? (
                                        <span style={{ color: settingsData?.overlapping === "None" ? "#FF0000" : "#fc8600", textDecoration: "underline", marginLeft: "25px", cursor: "pointer" }}
                                          onClick={() => {
                                            setSelectedAppointment(hasConflict);
                                            setIsModalOpen(true);
                                          }}>
                                          (Scheduling Conflict)
                                        </span>
                                      ) : null}

                                      {hasTimeZoneConflict ? (
                                        <span style={{ color: "red", textDecoration: "underline", marginLeft: "10px" }}>
                                          (TimeZone Conflict)
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="mv-10"></div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* {upcomingAppoinments?.length > 0 ? (
                                                <div className={`${isMobile || isLandscape ? "mt-30 mb-15" : "mt-10"}`}>
                                                    <Typography
                                                        variant="body"
                                                        style={{ fontSize: '14px', fontWeight: '700', color: '#020817' }}>Appointment Conflict</Typography>
                                                    <div className="d-flex flex-center appointments-container  mt-15">
                                                        {upcomingAppoinments?.map((appointment, index) => (
                                                            <div key={index} className="appointment-card">
                                                                <div className="d-flex flex-direction-column">
                                                                    <div className="client-name" >
                                                                        {selectedClient?.label !== "All" ? selectedClient?.label : ""}</div>
                                                                    <div className="appointment-date">{`${formatDate(appointment?.date)}, ${appointment?.time}`}</div>
                                                                    <div className="link ml-2 mv-3" style={{color:"blue", cursor:"pointer"}} 
                                                                    onClick={() => {
                                                                    setSelectedAppointment(appointment);
                                                                    setIsModalOpen(true);
                                                                    }}>
                                                                        View Details
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="mv-10"></div>
                                            )}

                                            {hasTimeZoneConflict && (
                                                <div className={`${isMobile || isLandscape ? "w-100 mt-10" : "w-35"}`}>
                                                    <Typography
                                                        variant="body"
                                                        style={{ fontSize: '16px', fontWeight: '700', color: '#020817' }}>
                                                        TimeZone Conflict
                                                    </Typography>
                                                    <div className="p-8 mt-10" style={{ marginRight: '20px', border: "1px solid red", borderRadius: "3px", fontSize: '14px', fontWeight: '600', color: '#020817' }}>
                                                        Client and staff are in different time zones.
                                                    </div>
                                                </div>
                                            )} */}

                    {appointmentType === "One Time" && upcomingAppoinments?.length > 0 && (
                      <div
                        style={{
                          background: settingsData?.overlapping === "None" ? "#ffe5e5" : "#fff1e0",
                          color: settingsData?.overlapping === "None" ? "#FF0000" : "#fc8600",
                          border: `1px solid ${settingsData?.overlapping === "None" ? "#ffb3b3" : "#ffd2a3"}`,
                          padding: "12px 16px",
                          borderRadius: "8px",
                          marginBottom: "12px",
                          marginTop: "14px",
                          fontWeight: 500
                        }}
                      >
                        {conflictGroup === "None"
                          ? "There are one or more scheduling conflicts with this appointment. Your company does not allow overlapping appointments. Please update your schedule."
                          : "There are one or more scheduling conflicts with this appointment. Please review them before you proceed."}
                      </div>
                    )}

                    {appointmentType === "One Time" && upcomingAppoinments?.length > 0 && (
                      <Card className={`${styles.appointmentSummaryCard} dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] dark:border-0`}>
                        <CardContent className="p-16">
                          {upcomingAppoinments?.map((appointment, index) => (
                            <div className={`${isMobile || isLandscape ? "mr-30 mv-10" : "mv-10"} dark:text-white text-[#020817]`} style={{ fontWeight: "500", fontSize: "14px" }}>
                              {`${settingsData?.timeFormat === '24hrs' ? moment(appointment?.time, 'h:mm A')?.format('HH:mm') : appointment?.time} on ${moment(appointment?.appointmentDate)?.format("MMMM DD, YYYY")} (${moment(appointment?.appointmentDate)?.format('ddd')})`}
                              <span style={{ color: settingsData?.overlapping === "None" ? "#FF0000" : "#fc8600", textDecoration: "underline", marginLeft: "25px", cursor: "pointer" }} onClick={() => { setSelectedAppointment(appointment); setIsModalOpen(true); }}>
                                (Scheduling Conflict) {appointmentId && appointmentType === "One Time" && (<span style={{ marginLeft: 5 }}>(Existing Appointment)</span>)}
                              </span>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}

                    <Card className={`${styles.appointmentSummaryCard} dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] dark:border-0`}>
                      <div className={styles.cardHeaders}>
                        <Typography variant="body" className={`${styles.cardTitles} dark:text-white`}>Appointment Summary</Typography>
                      </div>
                      <CardContent className="p-16">
                        <div className={styles.summaryGrid}>
                          <div className={styles.label}>Client:</div>
                          <div className={`${styles.value} dark:text-white`}>{selectedClient?.label}</div>

                          <div className={styles.label}>Shift:</div>
                          <div className={`${styles.value} dark:text-white`}>{shiftNames}</div>

                          <div className={styles.label}>Employee:</div>
                          <div className={`${styles.value} dark:text-white`}>{selectedEmployee?.label}</div>

                          <div className={styles.label}>Date:</div>
                          <div className={`${styles.value} dark:text-white`}>{getFormatedDate(selectedDate)}</div>

                          <div className={styles.label}>Time:</div>
                          <div className={`${styles.value} dark:text-white`}>
                            {startTime} to {endTime}
                          </div>

                          <div className={styles.label}>Type:</div>
                          <div className={`${styles.value} dark:text-white`}>{editAppointmentData ? editAppointmentData?.apoointmentDetails?.type : appointmentType}</div>

                          {appointmentType === "Recurring Appointment" && (
                            <>
                              <div className={styles.label}>Frequency:</div>
                              <div className={`${styles.value} dark:text-white`}>{capitalizeFirst(frequencyData)}</div>

                              <div className={styles.label}>Number of Times:</div>
                              <div className={`${styles.value} dark:text-white`}>{numberOfTime}</div>
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
            </CardContent>

            {isPast === true && (
              <div className="ph-24">
                <div className="font-medium bg-[#f8fafc] dark:bg-[#1A1F2C] dark:text-orange-600 p-10 mv-10">
                  The appointment time is in the past
                </div>
              </div>
            )}

            <div className={styles.buttonContainer}>
              {displayStep === 1 ? (
                <Button variant="outline" className={styles.cancelButton} onClick={() => navigate("/operations/manageAppointments")}>
                  Cancel
                </Button>
              ) : (
                <Button variant="outline" className={styles.cancelButton} onClick={() => setDisplayStep(displayStep - 1)}>
                  Back
                </Button>
              )}

              {displayStep < 4 ? (
                <Button
                  variant="contained"
                  className={styles.greenButton}
                  style={{ color: "white" }}
                  onClick={() => {
                    let nextStep = displayStep;
                    switch (displayStep) {
                      case 1:
                        if (clientName && clientName !== "All") {
                          nextStep = 2;
                        } else {
                          setErrors(prev => ({ ...prev, clientName: true }));
                          return;
                        }
                        break;

                      case 2:
                        if (shiftNames && shiftNames !== "Any") {
                          nextStep = 3;
                        } else {
                          setErrors(prev => ({ ...prev, shiftName: true }));
                          return;
                        }
                        break;

                      case 3:
                        if (empNames && empNames !== "All") {
                          nextStep = 4;
                        } else {
                          setErrors(prev => ({ ...prev, empName: true }));
                          return;
                        }
                        break;

                      default:
                        return;
                    }

                    setDisplayStep(nextStep);
                  }}
                >
                  Continue
                </Button>
              ) : (
                <Button
                  variant="contained"
                  style={{ color: "white" }}
                  className={styles.greenButton}
                  disabled={(appointmentType ==="Recurring Appointment" && recurringDates?.length === 0) || disableBooking || (appointmentId && appointmentType === "Recurring Appointment" && mode !== "this" && selectedIds?.length === 0)}
                  onClick={handleAddAppointments}
                >
                  {loader ? (<CircularProgress size={28} style={{ color: 'white' }} />) : appointmentId ? ('ReSchedule Appointment') : ('Book Appointment')}
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
      {selectedAppointment && (
        <ScheduleModal
          isModalOpen={isModalOpen}
          selectedAppointment={selectedAppointment}
          isMobile={isMobile}
          isLandscape={isLandscape}
          clientDetails={clientDetails}
          employeeDetails={empDetails}
          appoinments={appoinments}
          auth={auth}
          setIsModalOpen={setIsModalOpen}
          settingsData={settingsData}
          alerts={"allAlerts"}
          create={true}
        />
      )}
    </>
  )
}

const ScheduleModal = (props) => {
  const { isModalOpen } = props;
  const [isModalO, setIsModalO] = useState(false);


  useEffect(() => {
    if (isModalOpen) {
      setIsModalO(isModalOpen);
    } else {
      setTimeout(() => {
        setIsModalO(isModalOpen);
      }, 250);
    }

  }, [isModalOpen]);

  const handleOverlayClick = (e) => {
    if (e.target?.classList?.contains('modal-overlay')) {
      props?.setIsModalOpen(false);
      props?.auth.setEditAppointmentData({});
      props?.auth.setUpdateAppointmentData({});
    }
  };

  return isModalO ? (
    <div className={`modal-overlay active`} onClick={handleOverlayClick} >
      <div className={`modal-content dark:bg-[#1A1F2C] ${isModalOpen ? 'slideUpModal' : 'slideDownModal'}`}>
        {" "}
        <AppointmentModal {...props} />{" "}
      </div>
    </div>
  ) : null;
};

export default AppointmentsCard;